<script lang="ts" setup>
import { onMounted, ref, reactive, watch, onBeforeUnmount, computed } from 'vue';
import { PInput } from '@profeat/ui-kit';
import { onClickOutside, onKeyStroke, watchDebounced } from '@vueuse/core';

import { customerConstants } from '~/components/customers/helper';
import { ContactField } from '~/entities/contacts';
import { FilterBuilder } from '~/components/customers/filter-builder';

const { DEFAULT_FILTER_DEBOUNCE } = customerConstants;
const props = defineProps<{ field: ContactField; filterBuilder: FilterBuilder; refWrap: HTMLElement }>();
const emit = defineEmits<{
  (event: "applyFilter", visible?: boolean, withoutCall?: boolean): void;
  (event: "resetFilter", withoutCall?: boolean): void;
  (event: "clickOutside"): void;
  (event: "update:changed", val: boolean): void;
}>();

const filterRef = ref(null);
const isMountUpdate = ref(false);
const leftRange = ref("");
const rightRange = ref("");
const isUnmounted = ref(false);

onBeforeUnmount(() => {
  isUnmounted.value = true;
});

const rangeInit = reactive({
  left: "",
  right: "",
});

const isRangeChanged = computed(
  () => rangeInit.left != leftRange.value || rangeInit.right != rightRange.value
);

const setToFilter = () => {
  if (isMountUpdate.value) {
    emit("clickOutside");
    return;
  }

  const hasFilter = !!props.filterBuilder.getFiltersById(props.field.id).length;
  if (leftRange.value === "" && rightRange.value === "") {
    if (hasFilter) emit("resetFilter");
    else emit("clickOutside");

    return;
  }

  if (!leftRange.value || !rightRange.value) {
    props.filterBuilder.remove(props.field.id);
  }

  if (leftRange.value !== "") {
    props.filterBuilder.gte(
      props.field.id,
      String(leftRange.value).replaceAll(",", ".")
    );
  }
  if (rightRange.value !== "") {
    props.filterBuilder.lte(
      props.field.id,
      String(rightRange.value).replaceAll(",", ".")
    );
  }
  emit("applyFilter");
};

onKeyStroke(["Enter"], () => {
  setToFilter();
});
watchDebounced(
  [leftRange, rightRange],
  () => {
    if (isUnmounted.value || !isRangeChanged.value) return;
    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 3 }
);
onClickOutside(
  () => props.refWrap,
  () => {
    setToFilter();
  },
);
onMounted(() => {
  const filters = props.filterBuilder.getFiltersById(props.field.id);

  if (filters.length) {
    isMountUpdate.value = true;

    leftRange.value =
      (filters.find((f) => f.comparator === "gte")?.value as string) || "";
    rightRange.value =
      (filters.find((f) => f.comparator === "lte")?.value as string) || "";

    rangeInit.left = leftRange.value;
    rangeInit.right = rightRange.value;
  }
});
watch(
  [leftRange, rightRange],
  ([left, right]) => {
    const changed = (left !== rangeInit.left || right !== rangeInit.right) && !(right === '' && left === '');
    emit("update:changed", changed);
  },
  { deep: true }
);
</script>

<template>
  <div class="number-filter" ref="filterRef">
    <div class="values">
      <PInput
        class="input"
        v-model="leftRange"
        @update:model-value="() => (isMountUpdate = false)"
        text-size="small"
        size="medium"
        type="number"
        autofocus
        :placeholder="$t('customers.filter.range.from.placeholder')"
      />
      <hr class="divider" />
      <PInput
        class="input"
        v-model="rightRange"
        @update:model-value="() => (isMountUpdate = false)"
        text-size="small"
        size="medium"
        type="number"
        :placeholder="$t('customers.filter.range.to.placeholder')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.number-filter {
  .values {
    display: flex;
    grid-gap: 12px;
    align-items: center;

    .input {
      width: 132px;
    }
  }

  .divider {
    box-sizing: border-box;
    width: 8px;
    height: 1px;
    background: var(--common-asphalt, #91a0ae);
    outline: none;
    border: none;
  }
}
</style>
